import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Paper from "@mui/material/Paper";
import {
  Box,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import {
  addDays,
  format,
  startOfYesterday,
  subDays,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { ja } from "date-fns/locale";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutline,
  MoreVert,
  Undo,
} from "@mui/icons-material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { PiReadCvLogo } from "react-icons/pi";
import { LuArrowUpDown } from "react-icons/lu";

import Button from "../Button/Button";
import { CleaningEventStatus } from "types/type";
import Layout from "../templates/Layout"; // Layoutコンポーネントをインポート
import { pathToMapDetail, pathToRobotDetail } from "helpers/paths";
import ConfirmDialog from "../Form/ConfirmDialog";
import ListSearch from "../Form/ListSearch";
import useSort from "hooks/useSort";
import {
  useListCleaningEventByDateQuery,
  useUpdateCleaningEventStatus,
} from "hooks/worker/useCleaningEvent";
import {
  formatTimeJST,
  isSameDate,
  isThisWeekOrFuture,
  isTodayOrFuture,
} from "utils/date";
import { CleaningEventItem } from "types/response/cleaningEvent";
import { useListRobotQuery } from "hooks/worker/useRobot";
import { useListMapQuery } from "hooks/worker/useMap";
import { ManualStatus, ManualStatusType } from "types/cleaningEvent";
import { notify } from "lib/notify";
import { handleError } from "utils/error/handleError";
import {
  MAP_DELETED_LABEL,
  ROBOT_DELETED_LABEL,
} from "constraints/constraints";
import { useLoginUser } from "hooks/useLoginUser";
import { ConsumablePart } from "utils/robot/consumablePart";

type RobotListTab = "ERROR" | "COMPLETED" | "ALL";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2, 1),
}));

const getDateManualCleaning = (date: Date | undefined): string => {
  if (!date) {
    return "-";
  }

  return format(date, "M/d H:mm");
};

type CleaningEventView = CleaningEventItem & {
  mapName: string;
  robotName: string;
  batteryRatio: string;
  partsInfo: ConsumablePart[];
  dustboxRatio: number;
};

const CleaningRobotList = () => {
  const { isWorker } = useLoginUser();

  const [currentDate, setCurrentDate] = useState<Date>(startOfYesterday());

  // サーバーから取得したデータ類
  const { data: robots = [] } = useListRobotQuery();
  const { data: maps = [] } = useListMapQuery();
  const { data: cleaningEventFetchResult } = useListCleaningEventByDateQuery({
    baseDate: currentDate,
  });
  const mutation = useUpdateCleaningEventStatus({ baseDate: currentDate });

  // 清掃結果を表示用にロボット、マップと関連付け
  const cleaningEventViews: CleaningEventView[] = useMemo(() => {
    const robotMap = new Map(robots.map((r) => [r.robotId, r]));
    const mapMap = new Map(maps.map((m) => [m.mapId, m]));

    return (
      cleaningEventFetchResult?.cleaningEvents.map((e) => ({
        ...e,
        mapName: mapMap.get(e.mapId)?.mapName ?? MAP_DELETED_LABEL,
        batteryRatio:
          robotMap
            .get(e.robotId)
            ?.deviceStatuses.find((s) => s.type === "batteryRatio")?.status ??
          "-",
        robotName: robotMap.get(e.robotId)?.robotName ?? ROBOT_DELETED_LABEL,
        partsInfo: robotMap.get(e.robotId)?.consumableParts ?? [],
        dustboxRatio: 0, // TODO: 追加実装
      })) ?? []
    );
  }, [cleaningEventFetchResult?.cleaningEvents, robots, maps]);

  // UI制御用のstate
  const [selectedTab, setSelectedTab] = useState<RobotListTab>("ERROR");
  const [isDayView, setIsDayView] = useState(true);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [selectedCleaningEvent, setSelectedCleaningEvent] =
    useState<CleaningEventView>();
  const [hasErrorRobot, setHasErrorEvent] = useState(true);
  const [hasManualIncomplete, setHasManualIncomplete] = useState(true);
  const [enableMoreMenu, setEnableMoreMenu] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  type StatusFilterParams = {
    cleaningStatus: string;
  };
  const statusFilter = useCallback(
    (cleaningEvent: StatusFilterParams) => {
      const targetStatus = isDayView ? selectedTab : "ERROR";
      if (selectedTab === "ALL") return true;

      switch (targetStatus) {
        case "ERROR":
          return cleaningEvent.cleaningStatus !== "COMPLETED";
        default:
          return cleaningEvent.cleaningStatus === "COMPLETED";
      }

      // return (
      //   selectedTab === "ALL" || targetStatus === cleaningEvent.cleaningStatus
      // );
    },
    [selectedTab, isDayView]
  );

  const cleaningEventsFilterdByDate = cleaningEventViews.filter((c) => {
    if (!isDayView) return true;
    return isSameDate(c.cleaningAt, currentDate);
  });
  const filteredEvents = cleaningEventsFilterdByDate.filter(statusFilter);
  useEffect(() => {
    if (!filteredEvents) return;
    setHasErrorEvent(
      filteredEvents.some((e) => e.cleaningStatus !== "COMPLETED")
    );
    setHasManualIncomplete(filteredEvents.some((e) => isErrorTarget(e)));
  }, [filteredEvents]);

  useEffect(() => {
    setEnableMoreMenu(isWorker && hasErrorRobot);
  }, [isWorker, hasErrorRobot]);

  /**
   * 手動清掃切替処理
   */
  const onClickReport = (status: ManualStatusType) => {
    if (!selectedCleaningEvent) {
      notify("清掃イベントが選択されていません", "error");
      return;
    }
    mutation.mutate(
      {
        cleaningId: selectedCleaningEvent.cleaningEventId,
        status: status,
      },
      {
        onSuccess: () => {
          setAnchor(null);
          setConfirmDialogOpen(false);
          const message =
            status === ManualStatus.compoleted
              ? "更新しました"
              : "取り消しました";
          notify(message);
        },
        onError: (err) => {
          handleError(err);
        },
      }
    );
  };

  const onClickPrev = () => {
    setCurrentDate(subDays(currentDate, 1));
  };

  const onClickNext = () => {
    setCurrentDate(addDays(currentDate, 1));
  };

  const onClickWeekPrev = () => {
    setCurrentDate(subDays(currentDate, 7));
  };

  const onClickWeekNext = () => {
    setCurrentDate(addDays(currentDate, 7));
  };

  // テーブルの行
  const [searchedEvents, setSearchedEvents] = useState<CleaningEventView[]>([]);
  // 検索バーに入力された文字
  const [searched, setSearched] = useState("");

  const {
    order,
    orderBy,
    createSortHandler,
    stableSort,
    getComparator,
    orderByGetters,
  } = useSort<CleaningEventView>("asc", [(c) => c.cleaningAt]);

  const handleTabChange = (_: React.SyntheticEvent, value: RobotListTab) => {
    setSelectedTab(value);
  };

  const onClickMoreButton = (
    e: React.MouseEvent<HTMLElement>,
    cleaningEventId: string
  ) => {
    setAnchor(e.currentTarget);
    const currentEvent = cleaningEventViews.find(
      (e) => e.cleaningEventId === cleaningEventId
    );
    if (currentEvent) {
      setSelectedCleaningEvent(currentEvent);
    }
  };

  const onClickManualCleaning = useCallback(() => {
    if (!selectedCleaningEvent) {
      notify("清掃結果がありません", "error");
      return;
    }

    setAnchor(null);

    setConfirmDialogOpen(true);
  }, [selectedCleaningEvent]);

  const toggleView = () => {
    // setIsDayView((prev) => !prev);
    setSelectedTab("ERROR");
    setIsDayView(!isDayView);
  };

  return (
    <Layout title="清掃結果">
      {/* Layoutコンポーネントを追加し、タイトルを設定 */}
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
            alignItems: "center",
          }}
        >
          {isDayView ? (
            <Button
              variant="outlined"
              startIcon={<ArrowLeftOutlined />}
              onClick={onClickPrev}
              size="medium"
              sx={{ whiteSpace: "nowrap" }}
            >
              前日
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<ArrowLeftOutlined />}
              onClick={onClickWeekPrev}
              size="medium"
              sx={{ whiteSpace: "nowrap" }}
            >
              前週
            </Button>
          )}
          {isDayView ? (
            <Typography variant="h6" sx={{ mx: 1 }}>
              {format(currentDate, "yyyy/MM/dd(E)", { locale: ja })}
            </Typography>
          ) : (
            <Typography variant="h6" sx={{ mx: 1 }}>
              {format(
                startOfWeek(currentDate, { weekStartsOn: 1 }),
                "yyyy/MM/dd(E)",
                { locale: ja }
              )}
              ～
              {format(endOfWeek(currentDate, { weekStartsOn: 1 }), "MM/dd(E)", {
                locale: ja,
              })}
            </Typography>
          )}
          {isDayView ? (
            <Button
              variant="outlined"
              endIcon={<ArrowRightOutlined />}
              onClick={onClickNext}
              sx={{ whiteSpace: "nowrap" }}
              disabled={isTodayOrFuture(currentDate)}
            >
              翌日
            </Button>
          ) : (
            <Button
              variant="outlined"
              endIcon={<ArrowRightOutlined />}
              onClick={onClickWeekNext}
              sx={{ whiteSpace: "nowrap" }}
              disabled={isThisWeekOrFuture(currentDate)}
            >
              翌週
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", mt: 2, mb: 1, alignItems: "center" }}>
          <ListSearch
            initialRows={filteredEvents}
            searched={searched}
            setRows={setSearchedEvents}
            setSearched={setSearched}
            columnGetters={[
              (event) => event.mapName,
              (event) => event.robotName,
              (event) => getStatusManualCleaning(event.manualCleaningStatus),
            ]}
            sx={{ mr: 2 }}
          />
          <Button
            sx={{
              ml: "auto",
            }}
            variant="outlined"
            startIcon={<PiReadCvLogo />}
            onClick={toggleView}
          >
            {isDayView ? "1週間手作業清掃状況" : "清掃結果"}
          </Button>
        </Box>

        {isDayView ? (
          <div>
            <Tabs
              variant="fullWidth"
              value={selectedTab}
              onChange={handleTabChange}
            >
              <Tab label="エラー" value="ERROR" />
              <Tab label="完了" value="COMPLETED" />
              <Tab label="全て" value="ALL" />
            </Tabs>
          </div>
        ) : (
          <div>
            <Tabs
              variant="fullWidth"
              value={selectedTab}
              onChange={handleTabChange}
            >
              <Tab label="エラー" value="ERROR" />
            </Tabs>
          </div>
        )}

        <Divider />

        {searchedEvents.length > 0 ? (
          <TableContainer component={Paper}>
            <Table aria-label="simple table" cellPadding={1}>
              {isDayView ? (
                <TableHead>
                  <TableRow>
                    {hasManualIncomplete && (
                      <StyledTableCell width={40}>
                        <TableSortLabel
                          active={orderBy === "e.cleaningStatus"}
                          direction={
                            orderBy === "e.cleaningStatus" ? order : "asc"
                          }
                          hideSortIcon={true}
                          onClick={createSortHandler("e.cleaningStatus", [
                            (e) =>
                              e.manualCleaningStatus ===
                                ManualStatus.incompleted &&
                              isStatusError(e.cleaningStatus),
                          ])}
                        >
                          {orderBy !== "e.cleaningStatus" && (
                            <LuArrowUpDown style={{ opacity: 0.3 }} />
                          )}
                        </TableSortLabel>
                        {/* アイコン用 */}
                      </StyledTableCell>
                    )}
                    <StyledTableCell width={200}>
                      <TableSortLabel
                        active={orderBy === "e.mapName"}
                        direction={orderBy === "e.mapName" ? order : "asc"}
                        hideSortIcon={true}
                        onClick={createSortHandler("e.mapName", [
                          (e) => e.mapName || "",
                        ])}
                      >
                        設定マップ
                        {orderBy !== "e.mapName" && (
                          <LuArrowUpDown style={{ opacity: 0.3 }} />
                        )}
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell width={150}>
                      <TableSortLabel
                        active={orderBy === "e.deviceName"}
                        direction={orderBy === "e.deviceName" ? order : "asc"}
                        hideSortIcon={true}
                        onClick={createSortHandler("e.deviceName", [
                          (r) => r.robotName || "",
                        ])}
                      >
                        ロボット
                        {orderBy !== "e.deviceName" && (
                          <LuArrowUpDown style={{ opacity: 0.3 }} />
                        )}
                      </TableSortLabel>
                    </StyledTableCell>
                    {!isWorker && (
                      <>
                        <StyledTableCell width={150}>
                          <TableSortLabel
                            active={orderBy === "e.manualCleaningStatus"}
                            direction={
                              orderBy === "e.manualCleaningStatus"
                                ? order
                                : "asc"
                            }
                            hideSortIcon={true}
                            onClick={createSortHandler(
                              "e.manualCleaningStatus",
                              [
                                (e) =>
                                  getStatusManualCleaning(
                                    e.manualCleaningStatus
                                  ),
                              ]
                            )}
                          >
                            手作業清掃
                            {orderBy !== "e.manualCleaningStatus" && (
                              <LuArrowUpDown style={{ opacity: 0.3 }} />
                            )}
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell width={150}>
                          <TableSortLabel
                            active={orderBy === "e.manualCleaningDate"}
                            direction={
                              orderBy === "e.manualCleaningDate" ? order : "asc"
                            }
                            hideSortIcon={true}
                            onClick={createSortHandler("e.manualCleaningDate", [
                              (e) =>
                                getDateManualCleaning(e.manualCleaningDate) ||
                                "",
                            ])}
                          >
                            手作業
                            <br />
                            清掃日時
                            {orderBy !== "e.manualCleaningDate" && (
                              <LuArrowUpDown style={{ opacity: 0.3 }} />
                            )}
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell width={100}>
                          <TableSortLabel
                            active={orderBy === "e.batteryRatio"}
                            direction={
                              orderBy === "e.batteryRatio" ? order : "asc"
                            }
                            hideSortIcon={true}
                            onClick={createSortHandler("e.batteryRatio", [
                              (r) => r.batteryRatio || "",
                            ])}
                          >
                            充電状況
                            {orderBy !== "e.batteryRatio" && (
                              <LuArrowUpDown style={{ opacity: 0.3 }} />
                            )}
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell width={100}>
                          <TableSortLabel
                            active={orderBy === "e.cleaningStartTime"}
                            direction={
                              orderBy === "e.cleaningStartTime" ? order : "asc"
                            }
                            hideSortIcon={true}
                            onClick={createSortHandler("e.cleaningStartTime", [
                              (r) => r.cleaningStartTime || "",
                            ])}
                          >
                            清掃
                            <br />
                            開始時間
                            {orderBy !== "e.cleaningStartTime" && (
                              <LuArrowUpDown style={{ opacity: 0.3 }} />
                            )}
                          </TableSortLabel>
                        </StyledTableCell>
                      </>
                    )}
                    {enableMoreMenu && (
                      <StyledTableCell width={46}>
                        {/* その他ボタン用 */}
                      </StyledTableCell>
                    )}
                  </TableRow>
                </TableHead>
              ) : (
                <TableHead>
                  <TableRow>
                    {hasManualIncomplete && (
                      <StyledTableCell width={40}>
                        {/* アイコン用 */}
                      </StyledTableCell>
                    )}
                    <StyledTableCell width={200}>設定マップ</StyledTableCell>
                    <StyledTableCell width={150}>手作業清掃</StyledTableCell>
                    {enableMoreMenu && (
                      <StyledTableCell width={46}>
                        {/* その他ボタン用 */}
                      </StyledTableCell>
                    )}
                  </TableRow>
                </TableHead>
              )}

              {isDayView ? (
                <TableBody>
                  {stableSort(
                    searchedEvents,
                    getComparator(order, orderByGetters)
                  ).map((event) => {
                    return (
                      <TableRow
                        key={event.cleaningEventId}
                        sx={{
                          backgroundColor: (theme) =>
                            isErrorTarget(event)
                              ? theme.palette.error.light
                              : "inherit",
                        }}
                      >
                        {hasManualIncomplete && (
                          <StyledTableCell align="center">
                            {event.manualCleaningStatus !== "COMPLETED" &&
                              isStatusError(event.cleaningStatus) && (
                                <ReportProblemIcon
                                  fontSize="small"
                                  color="error"
                                  sx={{ width: "25px", height: "25px" }}
                                ></ReportProblemIcon>
                              )}
                          </StyledTableCell>
                        )}
                        <StyledTableCell>
                          <Link
                            to={pathToMapDetail(event.mapId)}
                            onClick={(e) => {
                              // マップが削除済みの場合、遷移しない
                              const hasMap = maps.some(
                                (m) => m.mapId === event.mapId
                              );
                              if (!hasMap) {
                                notify("削除されたマップです。", "warning");
                                e.preventDefault();
                              }
                            }}
                          >
                            {event.mapName}
                          </Link>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Link
                            to={pathToRobotDetail(event.robotId)}
                            onClick={(e) => {
                              // ロボットが削除済みの場合、遷移しない
                              const hasMap = robots.some(
                                (r) => r.robotId === event.robotId
                              );
                              if (!hasMap) {
                                notify("削除されたロボットです。", "warning");
                                e.preventDefault();
                              }
                            }}
                          >
                            {event.robotName}
                          </Link>
                        </StyledTableCell>
                        {!isWorker && (
                          <>
                            <StyledTableCell>
                              {getStatusManualCleaning(
                                event.manualCleaningStatus
                              )}
                            </StyledTableCell>
                            <StyledTableCell>
                              {getDateManualCleaning(event.manualCleaningDate)}
                            </StyledTableCell>
                            <StyledTableCell>
                              {event.batteryRatio} %
                            </StyledTableCell>
                            <StyledTableCell>
                              {formatTimeJST(event.cleaningStartTime)}
                            </StyledTableCell>
                          </>
                        )}
                        {enableMoreMenu && (
                          <StyledTableCell sx={{ p: 0 }} align="right">
                            {event.cleaningStatus !== "COMPLETED" && (
                              <IconButton
                                onClick={(e) =>
                                  onClickMoreButton(e, event.cleaningEventId)
                                }
                              >
                                <MoreVert
                                  sx={{ width: "30px", height: "30px" }}
                                />
                              </IconButton>
                            )}
                          </StyledTableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              ) : (
                <TableBody>
                  {stableSort(
                    searchedEvents,
                    getComparator(order, orderByGetters)
                  ).map((event) => {
                    return (
                      <TableRow
                        key={event.cleaningEventId}
                        sx={{
                          backgroundColor: (theme) => {
                            return isErrorTarget(event)
                              ? theme.palette.error.light
                              : "inherit";
                          },
                        }}
                      >
                        {hasManualIncomplete && (
                          <StyledTableCell align="center">
                            {event.manualCleaningStatus !== "COMPLETED" &&
                              isStatusError(event.cleaningStatus) && (
                                <ReportProblemIcon
                                  fontSize="small"
                                  color="error"
                                  sx={{ width: "25px", height: "25px" }}
                                ></ReportProblemIcon>
                              )}
                          </StyledTableCell>
                        )}
                        <StyledTableCell>
                          <Link to={pathToMapDetail(event.mapId)}>
                            {event.mapName}
                          </Link>
                        </StyledTableCell>
                        <StyledTableCell>
                          {getStatusManualCleaning(event.manualCleaningStatus)}
                        </StyledTableCell>
                        {enableMoreMenu && (
                          <StyledTableCell sx={{ p: 0 }} align="right">
                            {event.cleaningStatus !== "COMPLETED" && (
                              <IconButton
                                onClick={(e) =>
                                  onClickMoreButton(e, event.cleaningEventId)
                                }
                              >
                                <MoreVert
                                  sx={{ width: "30px", height: "30px" }}
                                />
                              </IconButton>
                            )}
                          </StyledTableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="h5" sx={{ margin: 4 }}>
            {getMessageByStatus(selectedTab, searched)}
          </Typography>
        )}
      </Box>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setAnchor(null)}
      >
        <Stack p={2}>
          {(!selectedCleaningEvent?.manualCleaningStatus ||
            selectedCleaningEvent?.manualCleaningStatus ===
              ManualStatus.incompleted) && (
            <Button
              startIcon={<CheckCircleOutline />}
              variant="text"
              onClick={onClickManualCleaning}
            >
              手動清掃完了
            </Button>
          )}
          {selectedCleaningEvent?.manualCleaningStatus ===
            ManualStatus.compoleted && (
            <Button
              startIcon={<Undo />}
              variant="text"
              onClick={onClickManualCleaning}
            >
              手動清掃完了取り消し
            </Button>
          )}
        </Stack>
      </Popover>
      <ConfirmDialog
        title="確認"
        onClickOk={() => {
          const oldStatus = selectedCleaningEvent?.manualCleaningStatus;
          const newStatus =
            oldStatus === ManualStatus.compoleted
              ? ManualStatus.incompleted
              : ManualStatus.compoleted;
          onClickReport(newStatus);
        }}
        onClickClose={() => {
          setConfirmDialogOpen(false);
        }}
        open={confirmDialogOpen}
      >
        <Typography>
          {selectedCleaningEvent?.manualCleaningStatus !==
          ManualStatus.compoleted
            ? "手動清掃を完了にします。"
            : "手動清掃完了を取り消します。"}
          よろしいですか。
        </Typography>
      </ConfirmDialog>
    </Layout>
  );
};

/**
 * 清掃イベントステータスがエラーかどうかを判定する。
 */
const isStatusError = (status: CleaningEventStatus): boolean => {
  return status !== "COMPLETED";
};

type IsErrorTargetParams = {
  cleaningStatus: CleaningEventStatus;
  manualCleaningStatus?: string;
};

/**
 * エラーとして目立たせる対象のイベントかどうかを判定する。
 */
const isErrorTarget = (event: IsErrorTargetParams): boolean => {
  return (
    isStatusError(event.cleaningStatus) &&
    event.manualCleaningStatus !== "COMPLETED"
  );
};

const getMessageByStatus = (
  status: RobotListTab,
  searchString: string
): string => {
  if (searchString) {
    return "検索結果はありません";
  }
  switch (status) {
    case "ERROR":
      return "エラーのロボットはありません";
    case "COMPLETED":
      return "清掃完了したロボットはありません";
    default:
      return "清掃記録はありません";
  }
};

const getStatusManualCleaning = (status?: string): string => {
  switch (status) {
    case "COMPLETED":
      return "清掃済";
    case "INCOMPLETE":
      return "未清掃";
    default:
      return "未清掃";
  }
};

export default CleaningRobotList;
